import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronRight, ChevronLeft, Plus, Check, Calendar, Trash2, Loader2, AlertTriangle, Baby } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { Button, Input } from '@/components/ui';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useAuth } from '@/features/auth';
import { supabase } from '@/lib/supabase';
import { getPredefinedCheckups, getCheckupPlanType, calculateDateForSSW } from '../data/predefinedCheckups';
import {
  ALL_BABY_APPOINTMENTS,
  calculateAppointmentDates,
  type SuggestionType,
} from '../data/babyAppointments';
import { APPOINTMENT_TYPE_COLORS } from '../types';
import type { AppointmentType } from '../types';

type UserRole = 'mother' | 'father' | 'partner';

interface AppointmentSetupWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (appointments: AppointmentToCreate[]) => Promise<boolean>;
}

export interface AppointmentToCreate {
  title: string;
  description: string;
  appointment_date: string;
  appointment_time?: string;
  appointment_type: AppointmentType;
  ssw_week?: number;
  is_all_day: boolean;
}

interface StepConfig {
  id: string;
  title: string;
  description: string;
  types: AppointmentType[];
  /** For baby mode: suggestion types instead of appointment types */
  suggestionTypes?: SuggestionType[];
}

// Step configs are built inside the component using t() - see getPregnancySteps / getBabySteps

// Helper to format date for display (locale set inside component)
const formatDateLocale = (dateStr: string, locale: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString(locale === 'en' ? 'en-GB' : 'de-DE', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
};

// Helper to format date as YYYY-MM-DD
const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface AppointmentItem {
  id: string;
  title: string;
  description: string;
  appointment_date: string;
  appointment_time: string;
  appointment_type: AppointmentType;
  ssw_week?: number;
  suggestion_type?: SuggestionType;
  days_from_birth?: number;
  is_all_day: boolean;
  enabled: boolean;
}

export function AppointmentSetupWizard({
  isOpen,
  onClose,
  onComplete,
}: AppointmentSetupWizardProps) {
  const { t, i18n } = useTranslation('calendar');
  const { currentPregnancy, lastCompletedPregnancy, appMode } = usePregnancyStore();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);

  // Build step configs using t()
  const PREGNANCY_STEPS: StepConfig[] = useMemo(() => [
    {
      id: 'welcome',
      title: t('setupWizard.pregnancyWelcome'),
      description: t('setupWizard.pregnancyWelcomeDescription'),
      types: [],
    },
    {
      id: 'checkup',
      title: t('setupWizard.categories.checkups'),
      description: t('setupWizard.categories.checkupsDescription'),
      types: ['checkup'],
    },
    {
      id: 'ultrasound',
      title: t('setupWizard.categories.ultrasound'),
      description: t('setupWizard.categories.ultrasoundDescription'),
      types: ['ultrasound'],
    },
    {
      id: 'doctor',
      title: t('setupWizard.categories.doctor'),
      description: t('setupWizard.categories.doctorDescription'),
      types: ['doctor'],
    },
    {
      id: 'personal',
      title: t('setupWizard.categories.personal'),
      description: t('setupWizard.categories.personalDescription'),
      types: ['course', 'personal'],
    },
  ], [t]);

  const BABY_STEPS: StepConfig[] = useMemo(() => [
    {
      id: 'welcome',
      title: t('setupWizard.babyWelcome'),
      description: t('setupWizard.babyWelcomeDescription'),
      types: [],
    },
    {
      id: 'u_examination',
      title: t('setupWizard.categories.uExams'),
      description: t('setupWizard.categories.uExamsDescription'),
      types: ['checkup'],
      suggestionTypes: ['u_examination'] as SuggestionType[],
    },
    {
      id: 'vaccination',
      title: t('setupWizard.categories.vaccinations'),
      description: t('setupWizard.categories.vaccinationsDescription'),
      types: ['doctor'],
      suggestionTypes: ['vaccination'] as SuggestionType[],
    },
    {
      id: 'official',
      title: t('setupWizard.categories.authorities'),
      description: t('setupWizard.categories.authoritiesDescription'),
      types: ['personal'],
      suggestionTypes: ['official'] as SuggestionType[],
    },
    {
      id: 'courses',
      title: t('setupWizard.categories.courses'),
      description: t('setupWizard.categories.coursesDescription'),
      types: ['course'],
      suggestionTypes: ['course', 'maternal_care'] as SuggestionType[],
    },
  ], [t]);
  const [appointments, setAppointments] = useState<Map<string, AppointmentItem>>(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAppointment, setNewAppointment] = useState({ title: '', date: '', time: '' });
  const [userRole, setUserRole] = useState<UserRole | null>(null);

  // Detect baby mode
  const isBabyMode = appMode === 'baby' && lastCompletedPregnancy?.completion_type === 'birth';
  const STEPS = isBabyMode ? BABY_STEPS : PREGNANCY_STEPS;

  // Fetch user role
  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user?.id) return;
      const { data } = await supabase
        .from('profiles')
        .select('user_role')
        .eq('id', user.id)
        .single();
      setUserRole(data?.user_role || null);
    };
    fetchUserRole();
  }, [user?.id]);

  // Get pregnancy type info for display (only for pregnancy mode)
  const pregnancyPlanType = useMemo(() => {
    if (isBabyMode) return null;
    return getCheckupPlanType(
      currentPregnancy?.is_high_risk || false,
      currentPregnancy?.child_count || 1
    );
  }, [isBabyMode, currentPregnancy?.is_high_risk, currentPregnancy?.child_count]);

  // Generate appointments based on mode
  const generatedAppointments = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (isBabyMode) {
      // BABY MODE: Generate from baby appointments data
      const birthDate = lastCompletedPregnancy?.actual_birth_date;
      if (!birthDate) return [];

      const birthDateObj = new Date(birthDate);

      return ALL_BABY_APPOINTMENTS
        .map((template) => {
          const dates = calculateAppointmentDates(template, birthDateObj);

          // Map suggestion type to appointment type
          const appointmentType: AppointmentType =
            template.suggestion_type === 'u_examination' ? 'checkup' :
            template.suggestion_type === 'vaccination' ? 'doctor' :
            template.suggestion_type === 'official' ? 'personal' :
            template.suggestion_type === 'course' ? 'course' :
            template.suggestion_type === 'maternal_care' ? 'doctor' : 'personal';

          return {
            id: `baby-${template.suggestion_type}-${template.category_order}`,
            title: template.title,
            description: template.description,
            appointment_date: formatLocalDate(dates.recommended_from),
            appointment_time: '',
            appointment_type: appointmentType,
            suggestion_type: template.suggestion_type,
            is_all_day: true,
            enabled: true,
            // For display: days from birth info
            days_from_birth: template.days_from_birth_start,
          };
        })
        .filter((apt) => {
          // Filter out appointments that are already past their window
          const aptDate = new Date(apt.appointment_date);
          return aptDate >= today || apt.days_from_birth < 0; // Keep pre-birth items
        });
    } else {
      // PREGNANCY MODE: Use existing logic
      if (!currentPregnancy?.expected_due_date) return [];

      const dueDate = new Date(currentPregnancy.expected_due_date);

      const checkups = getPredefinedCheckups(
        currentPregnancy.is_high_risk || false,
        currentPregnancy.child_count || 1
      );

      return checkups
        .filter((checkup) => {
          const checkupDate = calculateDateForSSW(dueDate, checkup.ssw_week);
          return checkupDate >= today;
        })
        .map((checkup) => {
          const appointmentDate = calculateDateForSSW(dueDate, checkup.ssw_week);
          return {
            id: `predefined-${checkup.ssw_week}-${checkup.appointment_type}`,
            title: checkup.title,
            description: checkup.description,
            appointment_date: formatLocalDate(appointmentDate),
            appointment_time: '',
            appointment_type: checkup.appointment_type,
            ssw_week: checkup.ssw_week,
            is_all_day: true,
            enabled: true,
          };
        });
    }
  }, [
    isBabyMode,
    lastCompletedPregnancy?.actual_birth_date,
    currentPregnancy?.expected_due_date,
    currentPregnancy?.is_high_risk,
    currentPregnancy?.child_count,
  ]);

  // Initialize appointments when wizard opens
  useEffect(() => {
    if (isOpen) {
      const initial = new Map<string, AppointmentItem>();
      generatedAppointments.forEach((apt) => {
        initial.set(apt.id, apt);
      });
      setAppointments(initial);
      setCurrentStep(0);
    }
  }, [isOpen, generatedAppointments]);

  if (!isOpen) return null;

  const stepConfig = STEPS[currentStep];
  const isWelcomeStep = currentStep === 0;
  const isLastAppointmentStep = currentStep === STEPS.length - 1;
  const isSuccessStep = currentStep === STEPS.length;

  // Filter appointments for current step
  const currentStepAppointments = Array.from(appointments.values()).filter((apt) => {
    if (isBabyMode && stepConfig?.suggestionTypes && apt.suggestion_type) {
      // In baby mode, filter by suggestion type
      return stepConfig.suggestionTypes.includes(apt.suggestion_type);
    }
    // In pregnancy mode, filter by appointment type
    return stepConfig?.types.includes(apt.appointment_type);
  });

  const toggleAppointment = (id: string) => {
    const apt = appointments.get(id);
    if (apt) {
      const newAppointments = new Map(appointments);
      newAppointments.set(id, { ...apt, enabled: !apt.enabled });
      setAppointments(newAppointments);
    }
  };

  const updateAppointmentDate = (id: string, newDate: string) => {
    const apt = appointments.get(id);
    if (apt) {
      const newAppointments = new Map(appointments);
      newAppointments.set(id, { ...apt, appointment_date: newDate });
      setAppointments(newAppointments);
    }
  };

  const updateAppointmentTime = (id: string, newTime: string) => {
    const apt = appointments.get(id);
    if (apt) {
      const newAppointments = new Map(appointments);
      newAppointments.set(id, { ...apt, appointment_time: newTime, is_all_day: !newTime });
      setAppointments(newAppointments);
    }
  };

  const removeAppointment = (id: string) => {
    const newAppointments = new Map(appointments);
    newAppointments.delete(id);
    setAppointments(newAppointments);
  };

  const addCustomAppointment = () => {
    if (!newAppointment.title.trim() || !newAppointment.date) return;

    const id = `custom-${Date.now()}`;
    const stepTypes = STEPS[currentStep].types;
    const newAppointments = new Map(appointments);
    newAppointments.set(id, {
      id,
      title: newAppointment.title.trim(),
      description: '',
      appointment_date: newAppointment.date,
      appointment_time: newAppointment.time,
      appointment_type: stepTypes[0],
      is_all_day: !newAppointment.time,
      enabled: true,
    });
    setAppointments(newAppointments);
    setNewAppointment({ title: '', date: '', time: '' });
    setShowAddForm(false);
  };

  const handleNext = () => {
    setShowAddForm(false);
    setNewAppointment({ title: '', date: '', time: '' });
    if (isLastAppointmentStep) {
      setCurrentStep(STEPS.length); // Go to success step
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setShowAddForm(false);
    setNewAppointment({ title: '', date: '', time: '' });
    setCurrentStep((prev) => Math.max(0, prev - 1));
  };

  const handleComplete = async () => {
    setIsSubmitting(true);
    const appointmentsToCreate: AppointmentToCreate[] = Array.from(appointments.values())
      .filter((apt) => apt.enabled)
      .map((apt) => ({
        title: apt.title,
        description: apt.description,
        appointment_date: apt.appointment_date,
        appointment_time: apt.appointment_time || undefined,
        appointment_type: apt.appointment_type,
        ssw_week: apt.ssw_week,
        is_all_day: apt.is_all_day,
      }));

    const success = await onComplete(appointmentsToCreate);
    setIsSubmitting(false);
    if (success) {
      onClose();
    }
  };

  // Count enabled for current step
  const enabledCountForStep = currentStepAppointments.filter((apt) => apt.enabled).length;

  // Total enabled count
  const totalEnabledCount = Array.from(appointments.values()).filter((apt) => apt.enabled).length;

  // Success step
  if (isSuccessStep) {
    const byType = new Map<AppointmentType, number>();
    appointments.forEach((apt) => {
      if (apt.enabled) {
        byType.set(apt.appointment_type, (byType.get(apt.appointment_type) || 0) + 1);
      }
    });

    return createPortal(
      <>
        <div className="fixed inset-0 bg-pp-dark/80 backdrop-blur-sm z-[9999]" />
        <div className="fixed inset-x-0 bottom-0 z-[9999] flex items-end sm:items-center justify-center sm:inset-0 sm:p-4">
          <div className="bg-pp-dark/35 backdrop-blur-md border-t border-white/25 rounded-t-2xl sm:rounded-[15px] w-full sm:max-w-md max-h-[85vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom sm:zoom-in-95 duration-300">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="text-lg font-semibold text-white">{t('setupWizard.summary')}</h2>
              <button onClick={onClose} className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-pp-teal/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-pp-teal" />
                </div>
                <h3 className="text-xl font-bold text-white">
                  {totalEnabledCount} {t('setupWizard.appointmentsSelected')}
                </h3>
                <p className="text-white/70 mt-1">
                  {t('setupWizard.readyToAdd')}
                </p>
              </div>

              <div className="space-y-3">
                {Array.from(byType.entries()).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between p-3 bg-white/10 rounded-lg">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${APPOINTMENT_TYPE_COLORS[type]}`}>
                      {t(`appointmentTypes.${type}`)}
                    </span>
                    <span className="text-white font-medium">{count} {t('setupWizard.appointments')}</span>
                  </div>
                ))}
              </div>

              <p className="text-sm text-white/50 mt-4 text-center">
                {t('setupWizard.editHint')}
              </p>
            </div>

            <div className="p-4 border-t border-white/10 flex gap-2" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}>
              <Button variant="secondary" className="flex-1 bg-white/20 text-white border-white/25 hover:bg-white/30" onClick={() => setCurrentStep(STEPS.length - 1)}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                {t('setupWizard.back')}
              </Button>
              <Button className="flex-1 bg-pp-orange hover:bg-pp-orange/90" onClick={handleComplete} disabled={isSubmitting || totalEnabledCount === 0}>
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Calendar className="w-4 h-4 mr-1" />
                    {t('setupWizard.addButton')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </>,
      document.body
    );
  }

  // Welcome step
  if (isWelcomeStep) {
    // Baby mode steps list
    const babyStepsList = [
      { num: 1, label: t('setupWizard.categoryLabels.uExams') },
      { num: 2, label: t('setupWizard.categoryLabels.vaccinations') },
      { num: 3, label: t('setupWizard.categoryLabels.authorities') },
      { num: 4, label: t('setupWizard.categoryLabels.courses') },
    ];

    // Pregnancy mode steps list
    const pregnancyStepsList = [
      { num: 1, label: t('setupWizard.categoryLabels.checkups') },
      { num: 2, label: t('setupWizard.categoryLabels.ultrasound') },
      { num: 3, label: t('setupWizard.categoryLabels.doctor') },
      { num: 4, label: t('setupWizard.categoryLabels.personal') },
    ];

    const stepsList = isBabyMode ? babyStepsList : pregnancyStepsList;

    return createPortal(
      <>
        <div className="fixed inset-0 bg-pp-dark/80 backdrop-blur-sm z-[9999]" />
        <div className="fixed inset-x-0 bottom-0 z-[9999] flex items-end sm:items-center justify-center sm:inset-0 sm:p-4">
          <div className="bg-pp-dark/35 backdrop-blur-md border-t border-white/25 rounded-t-2xl sm:rounded-[15px] w-full sm:max-w-md max-h-[85vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom sm:zoom-in-95 duration-300">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="text-lg font-semibold text-white">{stepConfig.title}</h2>
              <button onClick={onClose} className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="text-center">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
                  isBabyMode ? 'bg-pink-500/20' : 'bg-pp-orange/20'
                }`}>
                  {isBabyMode ? (
                    <Baby className="w-10 h-10 text-pink-400" />
                  ) : (
                    <Calendar className="w-10 h-10 text-pp-orange" />
                  )}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {t('setupWizard.title')}
                </h3>
                <p className="text-white/70 mb-6">
                  {isBabyMode
                    ? t('setupWizard.babyIntro')
                    : t('setupWizard.pregnancyIntro')}
                </p>

                <div className="text-left space-y-3 bg-white/10 rounded-lg p-4">
                  <p className="text-sm font-medium text-white">{t('setupWizard.stepsIntro')}</p>
                  <ul className="text-sm text-white/70 space-y-2">
                    {stepsList.map((step) => (
                      <li key={step.num} className="flex items-center gap-2">
                        <span className={`w-5 h-5 text-white rounded-full flex items-center justify-center text-xs ${
                          isBabyMode ? 'bg-pink-500' : 'bg-pp-orange'
                        }`}>
                          {step.num}
                        </span>
                        {step.label}
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="text-sm text-white/50 mt-4">
                  {isBabyMode
                    ? t('setupWizard.babyHint')
                    : t('setupWizard.pregnancyHint')}
                </p>

                {/* Pregnancy plan type indicator - only in pregnancy mode */}
                {!isBabyMode && userRole === 'mother' && pregnancyPlanType && (
                  <div className={`rounded-lg p-3 mt-4 text-left ${
                    pregnancyPlanType.type === 'standard'
                      ? 'bg-pp-teal/20'
                      : pregnancyPlanType.type === 'high_risk'
                      ? 'bg-amber-500/20'
                      : 'bg-purple-500/20'
                  }`}>
                    <div className="flex items-start gap-3">
                      {pregnancyPlanType.type !== 'standard' && (
                        <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          pregnancyPlanType.type === 'high_risk' ? 'text-amber-400' : 'text-purple-400'
                        }`} />
                      )}
                      <div>
                        <p className={`font-medium text-sm ${
                          pregnancyPlanType.type === 'standard'
                            ? 'text-pp-teal'
                            : pregnancyPlanType.type === 'high_risk'
                            ? 'text-amber-400'
                            : 'text-purple-400'
                        }`}>
                          {pregnancyPlanType.label}
                        </p>
                        <p className={`text-xs mt-1 ${
                          pregnancyPlanType.type === 'standard'
                            ? 'text-pp-teal/80'
                            : pregnancyPlanType.type === 'high_risk'
                            ? 'text-amber-300'
                            : 'text-purple-300'
                        }`}>
                          {pregnancyPlanType.description}
                        </p>
                        {pregnancyPlanType.type !== 'standard' && (
                          <p className={`text-xs mt-2 ${
                            pregnancyPlanType.type === 'high_risk' ? 'text-amber-400' : 'text-purple-400'
                          }`}>
                            {t('setupWizard.doctorHint')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-white/10 flex gap-2" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}>
              <Button variant="secondary" className="flex-1 bg-white/20 text-white border-white/25 hover:bg-white/30" onClick={onClose}>
                {t('setupWizard.cancel')}
              </Button>
              <Button className="flex-1 bg-pp-orange hover:bg-pp-orange/90" onClick={handleNext}>
                {t('setupWizard.start')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </>,
      document.body
    );
  }

  // Appointment steps
  return createPortal(
    <>
      <div className="fixed inset-0 bg-pp-dark/80 backdrop-blur-sm z-[9999]" />
      <div className="fixed inset-x-0 bottom-0 z-[9999] flex items-end sm:items-center justify-center sm:inset-0 sm:p-4">
        <div className="bg-pp-dark/35 backdrop-blur-md border-t border-white/25 rounded-t-2xl sm:rounded-[15px] w-full sm:max-w-md max-h-[85vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom sm:zoom-in-95 duration-300">
          {/* Header */}
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-white">{stepConfig.title}</h2>
              <button onClick={onClose} className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-white/70">{stepConfig.description}</p>

            {/* Progress */}
            <div className="flex gap-1 mt-3">
              {STEPS.slice(1).map((_, index) => (
                <div
                  key={index}
                  className={`flex-1 h-1 rounded-full ${
                    index < currentStep ? 'bg-pp-orange' : 'bg-white/20'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-3">
              {currentStepAppointments.map((apt) => (
                <div
                  key={apt.id}
                  className={`p-3 rounded-lg border-2 transition-colors ${
                    apt.enabled ? 'border-pp-orange bg-pp-orange/10' : 'border-white/20 bg-white/5'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => toggleAppointment(apt.id)}
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        apt.enabled
                          ? 'bg-pp-orange border-pp-orange text-white'
                          : 'border-white/30 bg-white/10'
                      }`}
                    >
                      {apt.enabled && <Check className="w-4 h-4" />}
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`font-medium ${apt.enabled ? 'text-white' : 'text-white/50'}`}>
                          {apt.title}
                        </span>
                        {apt.ssw_week && (
                          <span className="text-xs text-pp-orange bg-pp-orange/20 px-1.5 py-0.5 rounded">
                            SSW {apt.ssw_week}
                          </span>
                        )}
                      </div>

                      {apt.enabled && (
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div>
                            <label className="text-xs text-white/50 mb-1 block">{t('setupWizard.date')}</label>
                            <Input
                              type="date"
                              value={apt.appointment_date}
                              onChange={(e) => updateAppointmentDate(apt.id, e.target.value)}
                              className="text-sm bg-white/10 border-white/25 text-white"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-white/50 mb-1 block">{t('setupWizard.timeOptional')}</label>
                            <Input
                              type="time"
                              value={apt.appointment_time}
                              onChange={(e) => updateAppointmentTime(apt.id, e.target.value)}
                              className="text-sm bg-white/10 border-white/25 text-white"
                              placeholder="--:--"
                            />
                          </div>
                        </div>
                      )}

                      {!apt.enabled && (
                        <p className="text-xs text-white/40">
                          {formatDateLocale(apt.appointment_date, i18n.language)}
                          {apt.appointment_time && ` · ${apt.appointment_time}`}
                        </p>
                      )}
                    </div>

                    {apt.id.startsWith('custom-') && (
                      <button
                        onClick={() => removeAppointment(apt.id)}
                        className="w-8 h-8 rounded-full hover:bg-red-500/20 flex items-center justify-center text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {/* Add custom appointment */}
              {showAddForm ? (
                <div className="p-3 rounded-lg border-2 border-dashed border-white/30 space-y-3">
                  <Input
                    placeholder={t('setupWizard.label')}
                    value={newAppointment.title}
                    onChange={(e) => setNewAppointment({ ...newAppointment, title: e.target.value })}
                    autoFocus
                    className="bg-white/10 border-white/25 text-white placeholder:text-white/50"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="date"
                      value={newAppointment.date}
                      onChange={(e) => setNewAppointment({ ...newAppointment, date: e.target.value })}
                      className="bg-white/10 border-white/25 text-white"
                    />
                    <Input
                      type="time"
                      value={newAppointment.time}
                      onChange={(e) => setNewAppointment({ ...newAppointment, time: e.target.value })}
                      placeholder={t('setupWizard.timeOptional')}
                      className="bg-white/10 border-white/25 text-white"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" className="flex-1 bg-white/20 text-white border-white/25 hover:bg-white/30" onClick={() => setShowAddForm(false)}>
                      {t('setupWizard.cancel')}
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 bg-pp-orange hover:bg-pp-orange/90"
                      onClick={addCustomAppointment}
                      disabled={!newAppointment.title.trim() || !newAppointment.date}
                    >
                      {t('setupWizard.addButton')}
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="w-full p-3 rounded-lg border-2 border-dashed border-white/30 text-white/50 hover:border-pp-orange hover:text-pp-orange transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {t('setupWizard.addAppointment')}
                </button>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-white/10" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}>
            <div className="flex items-center justify-between mb-3 text-sm">
              <span className="text-white/50">
                {enabledCountForStep} {t('setupWizard.of')} {currentStepAppointments.length} {t('setupWizard.selected')}
              </span>
              <span className="text-pp-orange font-medium">
                {t('setupWizard.total')} {totalEnabledCount} {t('setupWizard.appointments')}
              </span>
            </div>

            <div className="flex gap-2">
              <Button variant="secondary" className="bg-white/20 text-white border-white/25 hover:bg-white/30" onClick={handleBack}>
                {currentStep === 1 ? (
                  t('setupWizard.cancel')
                ) : (
                  <>
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    {t('setupWizard.back')}
                  </>
                )}
              </Button>
              <Button className="flex-1 bg-pp-orange hover:bg-pp-orange/90" onClick={handleNext}>
                {isLastAppointmentStep ? (
                  t('setupWizard.finish')
                ) : (
                  <>
                    {t('setupWizard.next')}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
